import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Tooltip, Box, CircularProgress, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import TableChartIcon from '@mui/icons-material/TableChart';
import CodeIcon from '@mui/icons-material/Code';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import EmailIcon from '@mui/icons-material/Email';
import ArchiveIcon from '@mui/icons-material/Archive';
import ArticleIcon from '@mui/icons-material/Article';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import DataObjectIcon from '@mui/icons-material/DataObject';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { LINKED_PROJECT_PREFIX } from '../../constants/projectLink';
import { useFileViewer } from '../Viewer/FileViewerProvider';
import { fetchTimeeventThumbnail } from '../../Utils/timeEventsApi';

// Theme colors to match the rest of the application
const primaryColor = '#5757b0';
const lightPurple = '#f0f0ff';

// Styled thumbnail container with enhanced visual appeal
const StyledThumbnailContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isExist' && prop !== 'isHidden' && prop !== 'isSelected',
})(({ theme, isExist, isHidden, isSelected }) => ({
  position: 'relative',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
  border: `3px solid ${isSelected ? primaryColor : (isExist ? `${primaryColor}20` : '#ff666620')}`,
  boxShadow: isExist 
    ? (isSelected
      ? '0 0 0 4px rgba(87, 87, 176, 0.35), 0 0 0 8px rgba(87, 87, 176, 0.12), 0 8px 20px rgba(87, 87, 176, 0.22)'
      : '0 1px 5px rgba(87, 87, 176, 0.12)')
    : '0 1px 4px rgba(255, 102, 102, 0.12)',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  overflow: 'hidden',
  cursor: isExist ? 'pointer' : 'not-allowed',
  filter: !isExist || isHidden ? 'grayscale(80%)' : 'none',
  opacity: !isExist ? 0.6 : (isHidden ? 0.3 : 1),
  '&:hover': isExist ? {
    boxShadow: '0 2px 8px rgba(87, 87, 176, 0.18)',
    borderColor: primaryColor,
    '& .thumbnail-overlay': {
      opacity: 1,
    }
  } : {},
}));

// Styled image container for better image presentation
const StyledImageContainer = styled(Box)({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  '& img': {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'cover',
    borderRadius: '8px',
    transition: 'transform 0.3s ease',
  },
});

// Overlay for hover effects
const ThumbnailOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'linear-gradient(135deg, rgba(87, 87, 176, 0.8) 0%, rgba(119, 119, 208, 0.6) 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  transition: 'opacity 0.15s ease',
  borderRadius: '10px',
});

// Loading indicator
const LoadingContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  background: `linear-gradient(135deg, ${lightPurple} 0%, #ffffff 100%)`,
});

const Thumbnail = ({
  timeevent,
  sx,
  onOpenLinkedProject = null,
  isSelected = false,
}) => {
  const { id, isExist, hide, eventsource } = timeevent;
  const isLinkedProjectEvent = typeof eventsource === 'string' && eventsource.startsWith(LINKED_PROJECT_PREFIX);
  const linkedProjectId = isLinkedProjectEvent ? parseInt(eventsource.replace(LINKED_PROJECT_PREFIX, ''), 10) : null;
  const isInteractive = Boolean(isExist) || Boolean(isLinkedProjectEvent);
  
  // Debug logging to see what properties are available
  //console.log('Thumbnail timeevent props:', timeevent);

  const normalizedEventSource = useMemo(() => {
    if (!eventsource) {
      return '';
    }
    const lowerSource = eventsource.toLowerCase();
    return lowerSource.endsWith('.dataunfold')
      ? lowerSource.slice(0, -'.dataunfold'.length)
      : lowerSource;
  }, [eventsource]);

  const { openFileViewer } = useFileViewer();
  const [blobUrl, setBlobUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const thumbnailRef = useRef(null);
  const [thumbnailRequested, setThumbnailRequested] = useState(false);
  const activeThumbnailRequestRef = useRef(0);

  useEffect(() => {
    return () => {
      activeThumbnailRequestRef.current = 0;
    };
  }, []);

  const openViewer = useCallback((type, filename) => {
    openFileViewer({
      type,
      filename,
      eventId: id,
      filePath: eventsource,
      timeevent,
    });
  }, [eventsource, id, openFileViewer, timeevent]);
  
  // Function to check if file is an audio format
  const isAudioFormat = useCallback(() => {
    if (!normalizedEventSource) return false;
    const audioExtensions = ['.mp3', '.wav', '.m4a', '.ogg', '.flac', '.aac'];
    return audioExtensions.some(ext => normalizedEventSource.endsWith(ext));
  }, [normalizedEventSource]);

  // Function to check if file is a PDF
  const isPDFFormat = useCallback(() => {
    if (!eventsource) {
      console.log('No eventsource available for event:', id);
      return false;
    }
    const isPdf = normalizedEventSource.endsWith('.pdf');
    //console.log('Checking if PDF:', eventsource, 'Result:', isPdf);
    return isPdf;
  }, [normalizedEventSource, eventsource, id]);

  // Function to check if file is an image
  const isImageFormat = useCallback(() => {
    if (!normalizedEventSource) {
      console.log('No eventsource available for event:', id);
      return false;
    }
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff', '.tif', '.webp', '.svg'];
    return imageExtensions.some(ext => normalizedEventSource.endsWith(ext));
  }, [normalizedEventSource, id]);

  // Function to check if file is an Excel format
  const isExcelFormat = useCallback(() => {
    if (!normalizedEventSource) return false;
    const excelExtensions = ['.xlsx', '.xls', '.xlsm'];
    return excelExtensions.some(ext => normalizedEventSource.endsWith(ext));
  }, [normalizedEventSource]);

  // Function to check if file is HTML
  const isHTMLFormat = useCallback(() => {
    if (!normalizedEventSource) return false;
    const htmlExtensions = ['.html', '.htm'];
    return htmlExtensions.some(ext => normalizedEventSource.endsWith(ext));
  }, [normalizedEventSource]);

  // Function to check if file is a text format
  const isTextFormat = useCallback(() => {
    if (!normalizedEventSource) return false;
    const textExtensions = ['.txt', '.ini', '.hl7', '.log', '.md', '.json', '.xml', '.js', '.py', '.java', '.cpp', '.cs', '.php', '.rb', '.go', '.rs', '.swift', '.kt', '.ts', '.jsx', '.tsx', '.vue', '.css', '.scss', '.sass', '.less', '.sql', '.sh', '.bat', '.ps1', '.yaml', '.yml', '.toml', '.cfg', '.conf', '.properties'];
    return textExtensions.some(ext => normalizedEventSource.endsWith(ext));
  }, [normalizedEventSource]);

  // Function to check if file is a video format
  const isVideoFormat = useCallback(() => {
    if (!normalizedEventSource) return false;
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.avi', '.mov', '.wmv', '.flv', '.mkv', '.m4v', '.3gp', '.m3u8'];
    return videoExtensions.some(ext => normalizedEventSource.endsWith(ext));
  }, [normalizedEventSource]);

  // Function to check if file is an email format
  const isEmailFormat = useCallback(() => {
    if (!normalizedEventSource) return false;
    const emailExtensions = ['.msg', '.eml'];
    return emailExtensions.some(ext => normalizedEventSource.endsWith(ext));
  }, [normalizedEventSource]);

  // Function to check if file is a zip format
  const isZipFormat = useCallback(() => {
    if (!normalizedEventSource) return false;
    const zipExtensions = ['.zip', '.rar', '.7z', '.tar', '.gz', '.bz2'];
    return zipExtensions.some(ext => normalizedEventSource.endsWith(ext));
  }, [normalizedEventSource]);

  // Function to check if file is a Word format
  const isWordFormat = useCallback(() => {
    if (!normalizedEventSource) return false;
    const wordExtensions = ['.docx', '.doc'];
    return wordExtensions.some(ext => normalizedEventSource.endsWith(ext));
  }, [normalizedEventSource]);

  // Function to check if file is a PowerPoint format
  const isPowerPointFormat = useCallback(() => {
    if (!normalizedEventSource) return false;
    const pptExtensions = ['.pptx', '.ppt'];
    return pptExtensions.some(ext => normalizedEventSource.endsWith(ext));
  }, [normalizedEventSource]);

  // Function to check if file is a CSV format
  const isCSVFormat = useCallback(() => {
    if (!normalizedEventSource) return false;
    return normalizedEventSource.endsWith('.csv');
  }, [normalizedEventSource]);

  // Function to check if file is a JSON format
  const isJSONFormat = useCallback(() => {
    if (!normalizedEventSource) return false;
    return normalizedEventSource.endsWith('.json');
  }, [normalizedEventSource]);

  const openFile = useCallback(() => {
    if (isLinkedProjectEvent && linkedProjectId && typeof onOpenLinkedProject === 'function') {
      onOpenLinkedProject(linkedProjectId);
      return;
    }
    if (isExist) {
      const filename = eventsource ? eventsource.split('\\').pop().split('/').pop() : 'document';
      
      if (isPDFFormat()) {
        console.log('Opening PDF file:', filename, 'for event ID:', id);
        openViewer('pdf', filename);
      } else if (isImageFormat()) {
        console.log('Opening Image file:', filename, 'for event ID:', id);
        openViewer('image', filename);
      } else if (isAudioFormat()) {
        console.log('Opening Audio file:', filename, 'for event ID:', id);
        openViewer('audio', filename);
      } else if (isExcelFormat()) {
        console.log('Opening Excel file:', filename, 'for event ID:', id);
        openViewer('excel', filename);
      } else if (isHTMLFormat()) {
        console.log('Opening HTML file:', filename, 'for event ID:', id);
        openViewer('html', filename);
      } else if (isVideoFormat()) {
        console.log('Opening Video file:', filename, 'for event ID:', id);
        openViewer('video', filename);
      } else if (isEmailFormat()) {
        console.log('Opening Email file:', filename, 'for event ID:', id);
        openViewer('email', filename);
      } else if (isZipFormat()) {
        console.log('Opening Zip file:', filename, 'for event ID:', id);
        openViewer('zip', filename);
      } else if (isWordFormat()) {
        console.log('Opening Word file:', filename, 'for event ID:', id);
        openViewer('word', filename);
      } else if (isPowerPointFormat()) {
        console.log('Opening PowerPoint file:', filename, 'for event ID:', id);
        openViewer('powerpoint', filename);
      } else if (isCSVFormat()) {
        console.log('Opening CSV file:', filename, 'for event ID:', id);
        openViewer('csv', filename);
      } else if (isJSONFormat()) {
        console.log('Opening JSON file:', filename, 'for event ID:', id);
        openViewer('json', filename);
      } else if (isTextFormat()) {
        console.log('Opening Text file:', filename, 'for event ID:', id);
        openViewer('text', filename);
      } else {
        // For any other files, use the Other viewer
        console.log('Opening Other file:', filename, 'for event ID:', id);
        openViewer('other', filename);
      }
    }
  }, [onOpenLinkedProject, isLinkedProjectEvent, linkedProjectId, id, isExist, isPDFFormat, isImageFormat, isAudioFormat, isExcelFormat, isHTMLFormat, isVideoFormat, isEmailFormat, isZipFormat, isWordFormat, isPowerPointFormat, isCSVFormat, isJSONFormat, isTextFormat, eventsource, openViewer]);

  const handleThumbnailClick = useCallback(() => {
    openFile();
  }, [openFile]);

  const loadThumbnail = useCallback(async () => {
    if (!isExist || isAudioFormat() || thumbnailRequested || blobUrl) {
      return;
    }

    const requestId = Date.now();
    activeThumbnailRequestRef.current = requestId;
    setIsLoading(true);
    setThumbnailRequested(true);

    try {
      const blob = await fetchTimeeventThumbnail(id);
      if (activeThumbnailRequestRef.current !== requestId) {
        return;
      }
      const url = URL.createObjectURL(blob);
      setBlobUrl(url);
    } catch (error) {
      console.error('Error loading thumbnail:', error);
    } finally {
      if (activeThumbnailRequestRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, [blobUrl, id, isAudioFormat, isExist, thumbnailRequested]);

  useEffect(() => {
    if (!isExist || isAudioFormat()) return;

    // Set up intersection observer
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !thumbnailRequested && !blobUrl) {
          loadThumbnail();
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    const currentRef = thumbnailRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      // Clean up blob URL to prevent memory leaks
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl, id, isAudioFormat, isExist, loadThumbnail, thumbnailRequested]);

  return (
    <>
      <Tooltip 
        title={
          <Box sx={{ p: 1 }}>
            <Box sx={{ fontWeight: '500', mb: 0.5 }}>
              {isExist ? (
                isLinkedProjectEvent ? "Open linked project" :
                isPDFFormat() ? "Click to view PDF" : 
                isImageFormat() ? "Click to view image" : 
                isAudioFormat() ? "Click to play audio" :
                isExcelFormat() ? "Click to view Excel" :
                isHTMLFormat() ? "Click to view HTML" :
                isTextFormat() ? "Click to view text" :
                isVideoFormat() ? "Click to play video" :
                isEmailFormat() ? "Click to view email" :
                isZipFormat() ? "Click to browse archive" :
                isWordFormat() ? "Click to view Word document" :
                isPowerPointFormat() ? "Click to view presentation" :
                isCSVFormat() ? "Click to view CSV data" :
                isJSONFormat() ? "Click to view JSON data" :
                "Click to open file"
              ) : "File no longer exists"}
            </Box>
            <Box sx={{ fontSize: '0.75rem', opacity: 0.9 }}>
              {isLinkedProjectEvent ? "Linked Project" :
               isAudioFormat() ? "Audio File" : 
               isPDFFormat() ? "PDF Document" : 
               isImageFormat() ? "Image File" : 
               isExcelFormat() ? "Excel Spreadsheet" :
               isHTMLFormat() ? "HTML Document" :
               isTextFormat() ? "Text File" :
               isVideoFormat() ? "Video File" :
               isEmailFormat() ? "Email Message" :
               isZipFormat() ? "Archive File" :
               isWordFormat() ? "Word Document" :
               isPowerPointFormat() ? "PowerPoint Presentation" :
               isCSVFormat() ? "CSV Data" :
               isJSONFormat() ? "JSON Data" :
               "Document"}
            </Box>
          </Box>
        }
        placement="top"
        arrow
      >
        <StyledThumbnailContainer
          ref={thumbnailRef}
          isExist={isInteractive}
          isHidden={hide === 1}
          isSelected={isSelected}
          onClick={handleThumbnailClick}
          sx={sx}
        >
          <StyledImageContainer>
            {isLoading && !isAudioFormat() ? (
              <LoadingContainer>
                <CircularProgress 
                  size={24} 
                  sx={{ color: primaryColor }} 
                />
              </LoadingContainer>
            ) : blobUrl ? (
              <>
                <img 
                  src={blobUrl} 
                  alt="File Thumbnail" 
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '10px',
                  }}
                />
                <ThumbnailOverlay className="thumbnail-overlay">
                  <IconButton 
                    className="thumbnail-icon"
                    sx={{ 
                      color: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                      },
                    }}
                  >
                    <OpenInNewIcon />
                  </IconButton>
                </ThumbnailOverlay>
              </>
            ) : (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 2,
                    height: '100%',
                  }}
                >
                  {isAudioFormat() ? (
                    <>
                      <VolumeUpIcon 
                        sx={{ 
                          fontSize: 32, 
                          color: primaryColor, 
                          mb: 1,
                          opacity: 0.8
                        }} 
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: primaryColor,
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Audio
                      </Box>
                    </>
                  ) : isLinkedProjectEvent ? (
                    <>
                      <AccountTreeIcon
                        sx={{
                          fontSize: 32,
                          color: '#5e35b1',
                          mb: 1,
                          opacity: 0.9
                        }}
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: '#5e35b1',
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Project
                      </Box>
                    </>
                  ) : isPDFFormat() ? (
                    <>
                      <PictureAsPdfIcon 
                        sx={{ 
                          fontSize: 32, 
                          color: '#d32f2f', 
                          mb: 1,
                          opacity: 0.8
                        }} 
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: '#d32f2f',
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        PDF
                      </Box>
                    </>
                  ) : isImageFormat() ? (
                    <>
                      <ImageIcon 
                        sx={{ 
                          fontSize: 32, 
                          color: '#4caf50', 
                          mb: 1,
                          opacity: 0.8
                        }} 
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: '#4caf50',
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Image
                      </Box>
                    </>
                  ) : isExcelFormat() ? (
                    <>
                      <TableChartIcon 
                        sx={{ 
                          fontSize: 32, 
                          color: '#2e7d32', 
                          mb: 1,
                          opacity: 0.8
                        }} 
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: '#2e7d32',
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Excel
                      </Box>
                    </>
                  ) : isHTMLFormat() ? (
                    <>
                      <CodeIcon 
                        sx={{ 
                          fontSize: 32, 
                          color: '#ff9800', 
                          mb: 1,
                          opacity: 0.8
                        }} 
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: '#ff9800',
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        HTML
                      </Box>
                    </>
                  ) : isTextFormat() ? (
                    <>
                      <TextFieldsIcon 
                        sx={{ 
                          fontSize: 32, 
                          color: '#795548', 
                          mb: 1,
                          opacity: 0.8
                        }} 
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: '#795548',
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Text
                      </Box>
                    </>
                  ) : isVideoFormat() ? (
                    <>
                      <PlayCircleIcon 
                        sx={{ 
                          fontSize: 32, 
                          color: '#e91e63', 
                          mb: 1,
                          opacity: 0.8
                        }} 
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: '#e91e63',
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Video
                      </Box>
                    </>
                  ) : isEmailFormat() ? (
                    <>
                      <EmailIcon 
                        sx={{ 
                          fontSize: 32, 
                          color: '#3f51b5', 
                          mb: 1,
                          opacity: 0.8
                        }} 
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: '#3f51b5',
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Email
                      </Box>
                    </>
                  ) : isZipFormat() ? (
                    <>
                      <ArchiveIcon 
                        sx={{ 
                          fontSize: 32, 
                          color: '#607d8b', 
                          mb: 1,
                          opacity: 0.8
                        }} 
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: '#607d8b',
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Archive
                      </Box>
                    </>
                  ) : isWordFormat() ? (
                    <>
                      <ArticleIcon 
                        sx={{ 
                          fontSize: 32, 
                          color: '#2196f3', 
                          mb: 1,
                          opacity: 0.8
                        }} 
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: '#2196f3',
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Word
                      </Box>
                    </>
                  ) : isPowerPointFormat() ? (
                    <>
                      <SlideshowIcon 
                        sx={{ 
                          fontSize: 32, 
                          color: '#ff5722', 
                          mb: 1,
                          opacity: 0.8
                        }} 
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: '#ff5722',
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        PowerPoint
                      </Box>
                    </>
                  ) : isCSVFormat() ? (
                    <>
                      <TableChartIcon 
                        sx={{ 
                          fontSize: 32, 
                          color: '#4caf50', 
                          mb: 1,
                          opacity: 0.8
                        }} 
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: '#4caf50',
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        CSV
                      </Box>
                    </>
                  ) : isJSONFormat() ? (
                    <>
                      <DataObjectIcon 
                        sx={{ 
                          fontSize: 32, 
                          color: '#9c27b0', 
                          mb: 1,
                          opacity: 0.8
                        }} 
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: '#9c27b0',
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        JSON
                      </Box>
                    </>
                  ) : (
                    <>
                      <DescriptionIcon 
                        sx={{ 
                          fontSize: 32, 
                          color: primaryColor, 
                          mb: 1,
                          opacity: 0.8
                        }} 
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: primaryColor,
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Document
                      </Box>
                    </>
                  )}
                </Box>
                
                {isInteractive && (
                  <ThumbnailOverlay className="thumbnail-overlay">
                    <IconButton 
                      className="thumbnail-icon"
                      sx={{ 
                        color: 'white',
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.3)',
                        },
                      }}
                    >
                      <OpenInNewIcon />
                    </IconButton>
                  </ThumbnailOverlay>
                )}
              </>
            )}
          </StyledImageContainer>
          
          {/* Status indicator for file existence */}
          {!isExist && !isLinkedProjectEvent && (
            <Box
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: '#ff6666',
                boxShadow: '0 0 0 2px white',
              }}
            />
          )}
          
          {/* Hidden indicator */}
          {hide === 1 && isInteractive && (
            <Box
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: '#ffa500',
                boxShadow: '0 0 0 2px white',
              }}
            />
          )}
        </StyledThumbnailContainer>
      </Tooltip>

    </>
  );
};

export default React.memo(Thumbnail);
